import { useState } from 'react';
import { addDays } from 'date-fns';
import { toDate } from 'date-fns-tz';
import api from 'api';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { ModalButtonWithForm } from 'components/common';
import {
  Datepicker,
  EmailMessageEditor,
  FormGroup,
  RadioGroup,
  UserSelector
} from 'components/common/form';
import { UserPasswordReview } from 'components/users';

import type { EmailTemplateData } from 'shared-types';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  updateUsers: () => Promise<void>;
  userIds: number[];
}

const GroupUsersAdd = ({
  emailTemplates,
  emailTemplatesDefault,
  updateUsers,
  userIds
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { group },
    serverConfig: {
      accountAddForceExpirationDate,
      accountAddForceExpirationDateMaxDays,
      timeZone
    }
  } = useReduxState();

  const [newUsers, setNewUsers] = useState([]);
  const [userPasswords, setUserPasswords] = useState({});

  const handleNewUsers = (newUsersValue) => {
    setNewUsers(newUsersValue);
  };

  const handleSubmit = async (values) => {
    const response = await api.post(`/admin/groups/${group.id}/users`, {
      ...values,
      emailContent: values.email,
      userExpirationDate: values.userExpirationDate
        ? toDate(values.userExpirationDate, { timeZone })
        : null
    });

    let isWarning = false;
    let successMessage =
      `${response.data.addedUsers} ${messages.settingsAddUsersUsersAdded}, ` +
      `${response.data.newUsers} ${messages.settingsAddUsersAccountsCreated},` +
      `<br />${response.data.sendEmailsCount} ${messages.commonSendEmailInvitesSuccess}`;

    const errorEmails = Object.keys(response.data.sendEmailsErrors);

    if (errorEmails?.length) {
      isWarning = true;
      successMessage += `<br /><br /> ${messages.commonSendEmailInvitesError}`;
      errorEmails.forEach((errorEmail) => {
        successMessage += `<br />${errorEmail}`;
      });
    }

    await updateUsers();

    dispatch.addNotice({
      text: successMessage,
      type: isWarning ? 'warning' : 'success'
    });

    if (values.usernames?.length && response.data.newUsers) {
      const userPasswords = {};
      values.passwords.forEach((password, i) => {
        if (password) userPasswords[values.usernames[i]] = password;
      });

      setUserPasswords(userPasswords);
    }
  };

  const initialValues = emailTemplatesDefault
    ? {
        email: {
          ...emailTemplatesDefault.email,
          selectedTemplateIdentifier: emailTemplatesDefault.identifier
        },
        emails: [],
        emailNotificationType: 'no',
        userExpirationDate: null,
        usernames: []
      }
    : {};

  return (
    <>
      <ModalButtonWithForm
        buttonCategory="success"
        buttonIcon="plus"
        buttonSize="header"
        buttonText={messages.commonAddUsers}
        buttonTextHideBreakpoint={breakpoints.medium - 1}
        initialValues={initialValues}
        modalActionText={messages.commonAdd}
        modalTitle={messages.commonAddUsers}
        modalWidth="wide"
        onSubmit={handleSubmit}
      >
        {(formState) => (
          <>
            <UserSelector existingUsers={userIds} onNewUsers={handleNewUsers} />

            {accountAddForceExpirationDate && newUsers.length > 0 ? (
              <FormGroup
                htmlFor="userExpirationDate"
                label={messages.commonExpirationDateForNewUserAccounts}
                style={{ marginTop: 15 }}
              >
                <Datepicker
                  field="userExpirationDate"
                  id="userExpirationDate"
                  isClearable={false}
                  minDate={dateNow}
                  maxDate={addDays(
                    dateNow,
                    accountAddForceExpirationDateMaxDays
                  )}
                  required={true}
                  validateOnChange={true}
                  validateOnBlur={true}
                  style={{ maxWidth: 200 }}
                  placeholder={messages.commonExpirationDate}
                />
              </FormGroup>
            ) : null}

            {formState.values?.userSelector === 'emails' ? (
              <>
                <FormGroup label={messages.emailNotification}>
                  <RadioGroup
                    field="emailNotificationType"
                    options={[
                      {
                        id: `email-recipients-no`,
                        label: messages.emailNotificationLater,
                        value: 'no'
                      },
                      {
                        id: 'email-recipients-all',
                        label: messages.emailNotificationAllAbove,
                        value: 'all'
                      },
                      {
                        id: 'email-recipients-new',
                        label: messages.emailNotificationUninvited,
                        value: 'new'
                      }
                    ]}
                  />
                </FormGroup>

                <div
                  style={
                    formState.values?.emailNotificationType === 'no'
                      ? { height: 0, overflow: 'hidden', visibility: 'hidden' }
                      : {}
                  }
                >
                  <EmailMessageEditor
                    availableTemplates={emailTemplates}
                    id="share-users-add-send-message"
                    style={{ marginTop: 20 }}
                  />
                </div>
              </>
            ) : null}
          </>
        )}
      </ModalButtonWithForm>

      <UserPasswordReview
        onModalDismiss={() => setUserPasswords({})}
        passwords={userPasswords}
      />
    </>
  );
};

export default GroupUsersAdd;
